import React, { useState, useEffect } from "react";
import { getFunnel } from "../API's/Actioncreator";
import Loader from "../../../../presentationals/Loader/Loader.component";
// import Loader from "../../../Component/Common/Loader.component";
import StyledTooltip from "../common/StyledTooltip";
import moment from "moment";

/* clamp shorthand */
const c = (min, vw, max) => `clamp(${min}, ${vw}, ${max})`;

export default function FunnelVisualization({ selectedMonth, commonFilter }) {
  const [funnelData, setFunnelData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch funnel data when selectedMonth changes
  useEffect(() => {
    if (selectedMonth && selectedMonth.length > 0) {
      setLoading(true);
      setError(null);

      const monthMap = {
        Jan: 0, Feb: 1, Mar: 2, Apr: 3,
        May: 4, Jun: 5, Jul: 6, Aug: 7,
        Sep: 8, Oct: 9, Nov: 10, Dec: 11
      };

      const sortedMonths = [...selectedMonth].sort((a, b) => {
        const [mA, yA] = a.split(" ");
        const [mB, yB] = b.split(" ");

        return new Date(yA, monthMap[mA]) - new Date(yB, monthMap[mB]);
      });

      const [startMonthName, startYear] = sortedMonths[0].split(" ");
      const [endMonthName, endYear] =
        sortedMonths[sortedMonths.length - 1].split(" ");

      const startMonthIndex = monthMap[startMonthName];
      const endMonthIndex = monthMap[endMonthName];

      // const startDate = `01-${String(startMonthIndex + 1).padStart(2, "0")}-${startYear}`;

      const lastDay = new Date(endYear, endMonthIndex + 1, 0).getDate();
      // const endDate = `${lastDay}-${String(endMonthIndex + 1).padStart(2, "0")}-${endYear}`;

      const startDate = moment(commonFilter?.fromDate, "YYYY-MM-DD").format("DD-MM-YYYY");
            const endDate = moment(commonFilter?.toDate, "YYYY-MM-DD").format("DD-MM-YYYY");
      // Fetch funnel data
      new Promise((resolve, reject) => {
        getFunnel(startDate, endDate,commonFilter?.nbfc ?? "", resolve, reject);
      })
        .then((response) => {
          setLoading(false);
          if (response?.status === 200 && response?.data) {
            setFunnelData(response?.data);
          }
        })
        .catch((err) => {
          setLoading(false);
          setError("Failed to fetch funnel data: " + err?.message);
        });
    }
  }, [selectedMonth,commonFilter]);

  // Loading state
  if (loading) {
    return <Loader />;
  }

  // Error state
  if (error) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '400px',
        color: '#EF4444',
        fontSize: '16px'
      }}>
        {error}
      </div>
    );
  }

  // Map API data to component structure
  const currentData = funnelData ? {
    stages: [
      {
        label: "Total Due",
        value: (funnelData?.totalDue / 100000).toFixed(2) + "L",
        originalValue: funnelData?.totalDue || 0,
        percent: funnelData?.totalDuePct,
        color: "#3B82F6",
        barWidth: `${funnelData?.totalDuePct}%`,
        isCircle: false,
      },
      {
        label: "Collection Attempted",
        value: (funnelData?.collectionAttempted / 100000).toFixed(2) + "L",
        originalValue: funnelData?.collectionAttempted || 0,
        percent: funnelData?.collectionAttemptedPct,
        color: "#8B5CF6",
        barWidth: `${funnelData?.collectionAttemptedPct}%`,
        isCircle: false,
      },
      {
        label: "Successfully Collected",
        value: (funnelData?.successfullyCollected / 100000).toFixed(2) + "L",
        originalValue: funnelData?.successfullyCollected || 0,
        percent: funnelData?.successfullyCollectedPct,
        color: "#10B981",
        barWidth: `${Math.min(funnelData?.successfullyCollectedPct, 100)}%`,
        isCircle: false,
      },
      {
        label: "Pending Collection",
        value: (funnelData?.pendingCollection / 100000).toFixed(2) + "L",
        originalValue: funnelData?.pendingCollection || 0,
        percent: funnelData?.pendingCollectionPct,
        color: "#F59E0B",
        barWidth: `${funnelData?.pendingCollectionPct}%`,
        isCircle: false,
      },
      {
        label: "Defaulted",
        value: (funnelData?.defaulted / 100000).toFixed(2) + "L",
        originalValue: funnelData?.defaulted || 0,
        percent: funnelData?.defaultedPct,
        color: "#EF4444",
        barWidth: `${funnelData?.defaultedPct}%`,
        isCircle: true,
      },
    ],
    dropoffRows: [
      {
        label: "Due -> Attempted",
        sub: "Not attempted",
        value: (funnelData?.dueToAttemptedGap / 100000)?.toFixed(2) + "L",
        originalValue: funnelData?.dueToAttemptedGap || 0,
        drop: `${funnelData?.dueToAttemptedDropPct?.toFixed(1)}% drop`,
        valueColor: "#E7000B",
        dropColor: "#4A5565",
        bg: "#FEF2F2",
      },
      {
        label: "Attempted -> Collected",
        sub: "Failed attempts",
        value: (funnelData?.attemptedToCollectedGap / 100000)?.toFixed(2) + "L",
        originalValue: funnelData?.attemptedToCollectedGap || 0,
        drop: `${funnelData?.attemptedToCollectedDropPct?.toFixed(1)}% drop`,
        valueColor: "#D08700",
        dropColor: "#4A5565",
        bg: "#FEFCE8",
      },
      {
        label: "Success Rate",
        sub: "Collected from attempted",
        value: `${funnelData?.successRateAmongAttempted?.toFixed(1)}%`,
        drop: "Conversion efficiency",
        valueColor: "#00A63E",
        dropColor: "#4A5565",
        bg: "#F0FDF4",
      },
    ],
  } : {
    stages: [],
    dropoffRows: [],
  };

  // Use dynamic data from dummy data
  const funnelStages = currentData?.stages || [];
  const stageCards = currentData?.stages
    ? currentData.stages.map((stage) => ({
      label: stage?.label,
      value: `₹${stage?.value}`,
      originalValue: stage?.originalValue || 0,
      percent: `${stage?.percent}%`,
      color: stage?.color,
      border: stage?.color,
      bg: stage?.color + "20", // Add transparency for background
    }))
    : [];
  const dropoffRows = currentData?.dropoffRows || [];

  return (
    <div
      style={{
        fontFamily: "system-ui, -apple-system, sans-serif",
        backgroundColor: "#f8fafc",
        // border: "1px solid #e2e8f0",
        // padding: c("12px", "3vw", "28px"),
        boxSizing: "border-box",
        // borderRadius: "16px",
        marginTop: '40px',
        width: "100%",
      }}
    >
      {/* ── Title ── */}
      <div style={{ marginBottom: c("12px", "2vw", "18px") }}>
        <h4
          style={{
            margin: 0,
            fontSize: '20px',
            fontWeight: "600",
            color: "#1e293b",
          }}
        >
          Collection Funnel Analysis
        </h4>
        <p
          style={{
            margin: "8px 0 0",
            fontSize: c("11px", "1.3vw", "13px"),
            color: "#94a3b8",
          }}
        >

          {selectedMonth?.length > 0
            ? `Month: ${selectedMonth?.join(", ")}`
            : "No months selected"}
        </p>
      </div>

      {/* ── Top Rate Cards ── */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns:
            "repeat(auto-fit, minmax(min(160px, 100%), 1fr))",
          gap: c("8px", "1.5vw", "14px"),
          marginBottom: window.innerWidth > 1800 ? "25px" : '20px',
        }}
      >
        {[
          {
            bg: "#D8EBDE",
            border: "#D8EBDE",
            label: "Overall Conversion Rate",
            value: funnelData ? `${funnelData?.overallConversionRate?.toFixed(1)}%` : "80.0%",
            sub: "From Due to Collected",
            color: "#327E45",
          },
          {
            bg: "#FB2C361F",
            border: "#FB2C361F",
            label: "Drop-off Rate",
            value: funnelData ? `${funnelData?.dropOffRate?.toFixed(1)}%` : "20.0%",
            sub: "Pending + Defaulted",
            color: "#E84E4E",
          },
        ]?.map((card, i) => (
          <div
            key={i}
            style={{
              backgroundColor: card?.bg,
              border: `1px solid ${card?.border}`,
              borderRadius: c("8px", "1.5vw", "12px"),
              padding: `${c("14px", "2.5vw", "22px")} ${c("16px", "3vw", "26px")}`,
            }}
          >
            <div
              style={{
                fontSize: c("10px", "1.2vw", "12px"),
                color: card?.color,
                marginBottom: "6px",
              }}
            >
              {card?.label}
            </div>
            <div
              style={{
                fontSize: c("24px", "3.5vw", "34px"),
                fontWeight: "700",
                color: card?.color,
                marginBottom: "4px",
              }}
            >
              {card?.value}
            </div>
            <div
              style={{
                fontSize: c("10px", "1.2vw", "13px"),
                color: card?.color,
              }}
            >
              {card?.sub}
            </div>
          </div>
        ))}
      </div>

      {/* ── Funnel Flow ── */}
      <div
        style={{
          backgroundColor: "#fff",
          border: "1px solid #e2e8f0",
          borderRadius: c("8px", "1.5vw", "14px"),
          padding: c("14px", "2.5vw", "24px"),
          marginBottom: window.innerWidth > 1800 ? "25px" : '20px',
        }}
      >
        <h4
          style={{
            margin: `0 0 ${c("12px", "2vw", "20px")} 0`,
            fontSize: "20px",
            fontWeight: "600",
            color: "#0A0A0A",
          }}
        >
          Funnel Flow
        </h4>

        {currentData?.stages?.map((stage, idx) => (
          <div key={idx}>
            <div style={{ marginBottom: "6px" }}>
              {/* Label row */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: "6px",
                }}
              >
                <span
                  style={{
                    fontSize: "16px",
                    fontWeight: "500",
                    color: "#0A0A0A",
                  }}
                >
                  {stage.label}
                </span>
                <span
                  style={{
                    fontSize: "16px",
                    color: "#0A0A0A",
                  }}
                >
                  {stage?.percent?.toFixed(1)}%
                </span>
              </div>

              <div
                style={{
                  position: "relative",
                  height: "48px",
                  backgroundColor: "#f1f5f9",
                  borderRadius: "50px",
                  overflow: "hidden",
                }}
              >
                {/* {stage?.isCircle ? (
                  <div
                    style={{
                      position: "absolute",
                      left: 0,
                      top: 0,
                      width: c("40px", "5.5vw", "56px"),
                      height: "100%",
                      backgroundColor: stage?.color,
                      borderRadius: "50px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <StyledTooltip value={stage?.originalValue}>
                      <span
                        style={{
                          fontSize: c("9px", "1.1vw", "12px"),
                          fontWeight: "700",
                          color: "#fff",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {`₹${stage?.value}`}
                      </span>
                    </StyledTooltip>
                  </div>
                ) : (
                  <div
                    style={{
                      position: "absolute",
                      left: 0,
                      top: 0,
                      width: stage?.barWidth,
                      height: "100%",
                      backgroundColor: stage?.color,
                      borderRadius: "50px",
                      display: "flex",
                      alignItems: "center",
                      padding: "20px 22px",
                      minWidth: c("70px", "10vw", "100px"),
                      boxSizing: "border-box",
                      transition: "width 0.6s ease",
                    }}
                  >
                    <StyledTooltip value={stage?.originalValue}>
                      <span
                        style={{
                          fontSize: "16px",
                          fontWeight: "700",
                          color: "#fff",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {`₹${stage?.value}`}
                      </span>
                    </StyledTooltip>
                  </div>
                )} */}
                <div
                  style={{
                    position: "absolute",
                    left: 0,
                    top: 0,
                    width: stage?.percent?.toFixed(1) + "%",
                    height: "100%",
                    backgroundColor: stage?.color,
                    borderRadius: "50px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "start",
                  }}
                >
                  <StyledTooltip value={stage?.originalValue}>
                    <div
                      style={{
                        fontSize: c("9px", "1.1vw", "12px"),
                        fontWeight: "700",
                        color: stage?.percent?.toFixed(1) <= 6  ? "#000" :"#fff",
                        whiteSpace: "nowrap",
                        marginLeft:'12px'
                      }}
                    >
                      {stage?.value == "0.00L" ? "₹0" :`₹${stage?.value}`}
                    </div>
                  </StyledTooltip>
                </div>
              </div>
            </div>

            {/* Arrow connector */}
            {idx < funnelStages?.length - 1 && (
              <div
                style={{
                  textAlign: "center",
                  color: "#cbd5e1",
                  fontSize: c("14px", "2vw", "20px"),
                  margin: `${c("3px", "0.5vw", "5px")} 0 ${c("6px", "1vw", "10px")}`,
                }}
              >
                ↓
              </div>
            )}
          </div>
        ))}
      </div>

      {/* ── Stage-wise Analysis ── */}
      <div
        style={{
          backgroundColor: "#fff",
          border: "1px solid #e2e8f0",
          borderRadius: c("8px", "1.5vw", "14px"),
          padding: c("14px", "2.5vw", "24px"),
          marginBottom: window.innerWidth > 1800 ? "25px" : '20px',
        }}
      >
        <h4
          style={{
            margin: `0 0 ${c("12px", "2vw", "18px")} 0`,
            fontSize: '20px',
            fontWeight: "600",
            color: "#1e293b",
          }}
        >
          Stage-wise Analysis
        </h4>
        <div
          style={{
            display: "grid",
            gridTemplateColumns:
              "repeat(auto-fit, minmax(min(110px, 100%), 1fr))",
            gap: window.innerWidth > 1800 ? "20px" : "10px",
          }}
        >
          {stageCards && stageCards?.map((card, idx) => (
            <div
              key={idx}
              style={{
                border: `2px solid ${card?.border}`,
                borderRadius: c("8px", "1.2vw", "12px"),
                padding: `${c("10px", "1.8vw", "16px")} ${c("10px", "1.8vw", "18px")}`,
              }}
            >
              <div
                style={{
                  fontSize: window.innerWidth > 1800 ? "16px" : "12px",
                  color: "#64748b",
                  marginBottom: c("4px", "0.6vw", "7px"),
                }}
              >
                {card?.label}
              </div>
              <StyledTooltip value={card?.originalValue}>
                <div
                  style={{
                    fontSize: c("14px", "2vw", "19px"),
                    fontWeight: "700",
                    color: card?.color,
                    marginBottom: c("2px", "0.4vw", "5px"),
                  }}
                >
                  {card?.value}
                </div>
              </StyledTooltip>
              <div
                style={{
                  fontSize: window.innerWidth > 1800 ? "16px" : "12px",
                  color: "#94a3b8",
                }}
              >
                {card?.percent}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Drop-off Analysis ── */}
      <div
        style={{
          backgroundColor: "#fff",
          border: "1px solid #e2e8f0",
          borderRadius: c("8px", "1.5vw", "14px"),
          padding: c("14px", "2.5vw", "24px"),
        }}
      >
        <h4
          style={{
            margin: `0 0 ${c("12px", "2vw", "18px")} 0`,
            fontSize: '20px',
            fontWeight: "600",
            color: "#1e293b",
          }}
        >
          Drop-off Analysis
        </h4>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: c("8px", "1.2vw", "14px"),
          }}
        >
          {currentData?.dropoffRows?.map((row, idx) => (
            <div
              key={idx}
              style={{
                backgroundColor: row?.bg,
                borderRadius: c("8px", "1.2vw", "12px"),
                padding: `${c("10px", "1.8vw", "16px")} ${c("12px", "2vw", "20px")}`,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: "8px",
              }}
            >
              <div>
                <div
                  style={{
                    fontSize: window.innerWidth > 1800 ? "16px" : "12px",
                    fontWeight: "500",
                    color: row?.valueColor,
                    marginBottom: "2px",
                  }}
                >
                  {row?.label}
                </div>
                <div
                  style={{
                    fontSize: window.innerWidth > 1800 ? "14px" : "10px",
                    color: "#94a3b8",
                  }}
                >
                  {row?.sub}
                </div>
              </div>
              <div style={{ textAlign: "right" }}>
                {row?.originalValue ? (
                  <StyledTooltip value={row?.originalValue}>
                    <div
                      style={{
                        fontSize: c("13px", "1.8vw", "17px"),
                        fontWeight: "700",
                        color: row?.valueColor,
                        marginBottom: "2px",
                      }}
                    >
                      {`₹${row?.value}`}
                    </div>
                  </StyledTooltip>
                ) : (
                  <div
                    style={{
                      fontSize: c("13px", "1.8vw", "17px"),
                      fontWeight: "700",
                      color: row?.valueColor,
                      marginBottom: "2px",
                    }}
                  >
                    {row?.value}
                  </div>
                )}
                <div
                  style={{
                    fontSize: c("10px", "1.2vw", "12px"),
                    color: row.dropColor,
                  }}
                >
                  {row?.drop}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
